import React, { useState, useRef, useEffect } from 'react';
import { importApi } from '../lib/api';
import api, { fetchBlob } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Upload, FileSpreadsheet, ArrowRight, Check, AlertTriangle, Loader2, Download, RefreshCw, Package, Database } from 'lucide-react';
import { getApiErrorMessage } from '../lib/errorUtils';

const PRODUCT_FIELDS = [
  { value: '', label: '-- Ignorer --' },
  { value: 'reference', label: 'Reference *' },
  { value: 'designation', label: 'Designation' },
  { value: 'type_artifice', label: 'Type artifice' },
  { value: 'category', label: 'Categorie (F1-F4)' },
  { value: 'risk_class', label: 'Classe de risque (1.1G...)' },
  { value: 'un_code', label: 'Code ONU' },
  { value: 'nec', label: 'NEC (g)' },
  { value: 'homologation_number', label: 'N Homologation / Agrement' },
  { value: 'gross_weight', label: 'Poids brut (g)' },
  { value: 'net_weight', label: 'Poids net (kg)' },
  { value: 'caliber', label: 'Calibre (mm)' },
  { value: 'shots_count', label: 'Nombre de coups' },
  { value: 'duration', label: 'Duree (s)' },
  { value: 'safety_distance', label: 'Distance securite (m)' },
  { value: 'packaging', label: 'Conditionnement' },
  { value: 'manufacturer', label: 'Fabricant' },
  { value: 'supplier', label: 'Fournisseur' },
  { value: 'purchase_price', label: 'Prix achat HT' },
  { value: 'public_price', label: 'Prix public TTC' },
  { value: 'symbole', label: 'Symbole' },
];

const CLIENT_FIELDS = [
  { value: '', label: '— Ignorer —' },
  { value: 'name', label: 'Nom / Raison sociale *' },
  { value: 'code', label: 'Code' },
  { value: 'contact_name', label: 'Contact' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Téléphone' },
  { value: 'address', label: 'Adresse' },
  { value: 'postal_code', label: 'Code postal' },
  { value: 'city', label: 'Ville' },
  { value: 'siret', label: 'SIRET' },
];

const SUPPLIER_FIELDS = [...CLIENT_FIELDS];

const ImportPage = () => {
  const [step, setStep] = useState(1);
  const [target, setTarget] = useState('products');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const fields = target === 'products' ? PRODUCT_FIELDS : target === 'clients' ? CLIENT_FIELDS : SUPPLIER_FIELDS;

  const handleFileSelect = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    setUploading(true);
    try {
      const res = await importApi.preview(f);
      setPreview(res.data);
      // Auto-map columns with matching names + Artifices.xlsx aliases
      const COLUMN_ALIASES = {
        'reference': 'reference',
        'description': 'designation',
        'designation': 'designation',
        'typeartifice': 'type_artifice',
        'categorie': 'category',
        'category': 'category',
        'classerisque': 'risk_class',
        'riskclass': 'risk_class',
        'codeonu': 'un_code',
        'uncode': 'un_code',
        'agrement': 'homologation_number',
        'numeroagrement': 'homologation_number',
        'numerohomologation': 'homologation_number',
        'homologationnumber': 'homologation_number',
        'masse': 'nec',
        'nec': 'nec',
        'necparelement': 'nec',
        'massenette': 'gross_weight',
        'poidsbrut': 'gross_weight',
        'grossweight': 'gross_weight',
        'poidsnet': 'net_weight',
        'netweight': 'net_weight',
        'calibre': 'caliber',
        'caliber': 'caliber',
        'nombredecoups': 'shots_count',
        'shotscount': 'shots_count',
        'duree': 'duration',
        'duration': 'duration',
        'distancesecurite': 'safety_distance',
        'safetydistance': 'safety_distance',
        'conditionnement': 'packaging',
        'packaging': 'packaging',
        'fabricant': 'manufacturer',
        'manufacturer': 'manufacturer',
        'fournisseur': 'supplier',
        'supplier': 'supplier',
        'prixachat': 'purchase_price',
        'purchaseprice': 'purchase_price',
        'prixpublic': 'public_price',
        'publicprice': 'public_price',
        'symbole': 'symbole',
      };
      const autoMap = {};
      const usedFields = new Set();
      for (const col of res.data.columns) {
        const clean = col.toLowerCase().replace(/[^a-z]/g, '');
        // Try alias first
        if (COLUMN_ALIASES[clean] && !usedFields.has(COLUMN_ALIASES[clean])) {
          autoMap[col] = COLUMN_ALIASES[clean];
          usedFields.add(COLUMN_ALIASES[clean]);
        } else {
          // Fallback: exact match with field value
          const match = fields.find(field => field.value && !usedFields.has(field.value) && field.value.toLowerCase().replace(/[^a-z]/g, '') === clean);
          if (match) {
            autoMap[col] = match.value;
            usedFields.add(match.value);
          }
        }
      }
      setMapping(autoMap);
      setStep(2);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erreur de lecture du fichier'));
    }
    setUploading(false);
  };

  const handleImport = async () => {
    const hasMapped = Object.values(mapping).some(v => v);
    if (!hasMapped) { toast.error('Mappez au moins une colonne'); return; }
    setImporting(true);
    try {
      const res = await importApi.execute(file, target, mapping);
      setResult(res.data);
      setStep(3);
      toast.success(`Import terminé : ${res.data.created} créé(s), ${res.data.updated} mis à jour`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erreur lors de l\'import'));
    }
    setImporting(false);
  };

  const reset = () => { setStep(1); setFile(null); setPreview(null); setMapping({}); setResult(null); };

  return (
    <div className="space-y-6" data-testid="import-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading text-white">Import CSV / Excel</h1>
          <p className="text-zinc-400 text-sm">Importez vos données en masse</p>
        </div>
        {step > 1 && <Button variant="outline" className="border-zinc-700" onClick={reset}>Recommencer</Button>}
      </div>

      {/* Import Management / Undo */}
      <ImportManager />

      {/* Steps indicator */}
      <div className="flex items-center gap-4">
        {['Fichier', 'Mapping', 'Résultat'].map((label, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
              step > i + 1 ? 'bg-green-500 text-black' : step === i + 1 ? 'bg-amber-500 text-black' : 'bg-zinc-800 text-zinc-500'
            }`}>{step > i + 1 ? <Check className="w-4 h-4" /> : i + 1}</div>
            <span className={`text-sm ${step === i + 1 ? 'text-white' : 'text-zinc-500'}`}>{label}</span>
            {i < 2 && <ArrowRight className="w-4 h-4 text-zinc-700" />}
          </div>
        ))}
      </div>

      {/* Step 1: File selection */}
      {step === 1 && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-6 space-y-4">
            <div className="space-y-2">
              <label className="text-zinc-300 text-sm font-medium">Type de données</label>
              <Select value={target} onValueChange={setTarget}>
                <SelectTrigger className="w-64 bg-zinc-950 border-zinc-800 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="products">Articles / Produits</SelectItem>
                  <SelectItem value="clients">Clients</SelectItem>
                  <SelectItem value="suppliers">Fournisseurs</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div
              onClick={() => fileRef.current?.click()}
              className="border-2 border-dashed border-zinc-700 rounded-lg p-12 text-center cursor-pointer hover:border-amber-500/50 transition-colors"
              data-testid="import-dropzone"
            >
              {uploading ? (
                <Loader2 className="w-12 h-12 text-amber-400 mx-auto mb-3 animate-spin" />
              ) : (
                <FileSpreadsheet className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
              )}
              <p className="text-white font-medium">{file ? file.name : 'Cliquez pour sélectionner un fichier'}</p>
              <p className="text-zinc-500 text-sm mt-1">CSV ou Excel (.xlsx)</p>
              <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={handleFileSelect} data-testid="import-file-input" />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Column mapping */}
      {step === 2 && preview && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white text-lg flex items-center gap-2">
              <Upload className="w-5 h-5 text-amber-400" />
              Mapping des colonnes — {preview.filename}
              <Badge variant="outline" className="ml-2 text-zinc-400">{preview.columns.length} colonnes</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-zinc-400 text-sm">Associez chaque colonne de votre fichier à un champ PyroStock :</p>
            <div className="grid grid-cols-2 gap-3">
              {preview.columns.map(col => (
                <div key={col} className="flex items-center gap-3 bg-zinc-950 rounded-lg p-3">
                  <span className="text-cyan-400 font-mono text-sm min-w-[140px] truncate" title={col}>{col}</span>
                  <ArrowRight className="w-4 h-4 text-zinc-600 flex-shrink-0" />
                  <Select value={mapping[col] || ''} onValueChange={(v) => setMapping({...mapping, [col]: v})}>
                    <SelectTrigger className="bg-zinc-900 border-zinc-800 text-white text-sm flex-1">
                      <SelectValue placeholder="— Ignorer —" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      {fields.map(f => <SelectItem key={f.value} value={f.value || 'ignore'}>{f.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>

            {/* Preview table */}
            <div className="mt-4">
              <p className="text-zinc-500 text-xs mb-2">Aperçu des premières lignes :</p>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800">
                      {preview.columns.map(col => <TableHead key={col} className="text-zinc-400 text-xs">{col}</TableHead>)}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.preview.slice(0, 5).map((row, i) => (
                      <TableRow key={i} className="border-zinc-800">
                        {preview.columns.map(col => <TableCell key={col} className="text-zinc-300 text-xs">{row[col] || ''}</TableCell>)}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" className="border-zinc-700" onClick={() => setStep(1)}>Retour</Button>
              <Button onClick={handleImport} className="bg-amber-500 hover:bg-amber-400 text-black" disabled={importing} data-testid="execute-import-button">
                {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                Importer {preview.row_count}+ lignes
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Results */}
      {step === 3 && result && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-6 space-y-4">
            <div className="text-center py-4">
              <Check className="w-16 h-16 text-green-400 mx-auto mb-3" />
              <h2 className="text-xl text-white font-heading">Import terminé</h2>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 text-center">
                <p className="text-3xl font-bold text-green-400">{result.created}</p>
                <p className="text-sm text-zinc-400">Créé(s)</p>
              </div>
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 text-center">
                <p className="text-3xl font-bold text-amber-400">{result.updated}</p>
                <p className="text-sm text-zinc-400">Mis à jour</p>
              </div>
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-center">
                <p className="text-3xl font-bold text-red-400">{result.errors?.length || 0}</p>
                <p className="text-sm text-zinc-400">Erreur(s)</p>
              </div>
            </div>
            {result.errors?.length > 0 && (
              <div className="space-y-1 mt-4">
                <p className="text-red-400 text-sm font-semibold flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> Erreurs :</p>
                {result.errors.map((e, i) => <p key={i} className="text-zinc-400 text-xs font-mono">{e}</p>)}
              </div>
            )}
            <div className="flex justify-center gap-3 pt-4">
              <Button onClick={reset} className="bg-amber-500 hover:bg-amber-400 text-black">Nouvel import</Button>
              <Button variant="outline" className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('pyrostock_token');
                    const API_URL = process.env.REACT_APP_BACKEND_URL;
                    const res = await fetch(`${API_URL}/api/import/undo-last`, {
                      method: 'POST', credentials: 'include',
                      headers: token ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' },
                    });
                    if (!res.ok) throw new Error((await res.json()).detail || 'Erreur');
                    const data = await res.json();
                    toast.success(`Import annule : ${data.deleted} element(s) supprime(s)`);
                    reset();
                  } catch (err) { toast.error(err.message || 'Erreur annulation'); }
                }}
                data-testid="undo-import-btn">
                <AlertTriangle className="w-4 h-4 mr-2" /> Annuler cet import
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* === LIBRARY COMPLETION TOOL === */}
      <LibraryCompletionTool />

      {/* === ACCESS DATABASE IMPORT === */}
      <AccessImportTool />
    </div>
  );
};


const ImportManager = () => {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [undoing, setUndoing] = useState(null);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('pyrostock_token');
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API_URL}/api/import/history`, {
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      });
      if (res.ok) setHistory(await res.json());
    } catch {}
    setLoading(false);
  };

  const handleUndo = async (type, id) => {
    if (!window.confirm('Etes-vous sur de vouloir annuler cet import ? Les elements importes seront supprimes.')) return;
    setUndoing(id || type);
    try {
      const token = localStorage.getItem('pyrostock_token');
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      let url;
      if (type === 'batch' && id) url = `${API_URL}/api/import/undo/${id}`;
      else if (type === 'access_mdb') url = `${API_URL}/api/import/undo-source/access_mdb`;
      else if (type === 'Artifices.xlsx') url = `${API_URL}/api/import/undo-source/Artifices.xlsx`;
      else url = `${API_URL}/api/import/undo-last`;
      
      const res = await fetch(url, {
        method: 'POST', credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' },
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Erreur');
      const data = await res.json();
      const count = data.deleted || (data.details ? Object.values(data.details).reduce((a, b) => a + b, 0) : 0);
      toast.success(`Import annule : ${count} element(s) supprime(s)`);
      fetchHistory();
    } catch (err) { toast.error(err.message || 'Erreur annulation'); }
    setUndoing(null);
  };

  useEffect(() => { fetchHistory(); }, []);

  if (!history) return null;

  const hasImports = (history.batches?.length > 0) || history.mdb_imported > 0 || history.xlsx_imported > 0;
  if (!hasImports) return null;

  return (
    <Card className="bg-zinc-900/50 border-zinc-800" data-testid="import-manager">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-white font-semibold text-sm flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400" />
            Gestion des imports
          </p>
          <Button size="sm" variant="ghost" className="text-zinc-400 text-xs" onClick={fetchHistory} disabled={loading}>
            <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} /> Actualiser
          </Button>
        </div>

        <div className="space-y-2">
          {/* Artifices.xlsx source */}
          {history.xlsx_imported > 0 && (
            <div className="flex items-center justify-between bg-zinc-950 p-3 rounded-lg border border-zinc-800">
              <div>
                <p className="text-white text-sm">Artifices.xlsx</p>
                <p className="text-zinc-500 text-xs">{history.xlsx_imported} produits importes</p>
              </div>
              <Button size="sm" variant="outline" className="border-red-500/50 text-red-400 hover:bg-red-500/10 text-xs"
                onClick={() => handleUndo('Artifices.xlsx')} disabled={undoing === 'Artifices.xlsx'}
                data-testid="undo-xlsx-import">
                {undoing === 'Artifices.xlsx' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <AlertTriangle className="w-3 h-3 mr-1" />}
                Annuler
              </Button>
            </div>
          )}

          {/* Access MDB source */}
          {history.mdb_imported > 0 && (
            <div className="flex items-center justify-between bg-zinc-950 p-3 rounded-lg border border-zinc-800">
              <div>
                <p className="text-white text-sm">Base Access (.mdb)</p>
                <p className="text-zinc-500 text-xs">{history.mdb_imported} produits importes</p>
              </div>
              <Button size="sm" variant="outline" className="border-red-500/50 text-red-400 hover:bg-red-500/10 text-xs"
                onClick={() => handleUndo('access_mdb')} disabled={undoing === 'access_mdb'}
                data-testid="undo-mdb-import">
                {undoing === 'access_mdb' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <AlertTriangle className="w-3 h-3 mr-1" />}
                Annuler
              </Button>
            </div>
          )}

          {/* Recent CSV batches */}
          {history.batches?.filter(b => b.target !== 'access_mdb').slice(0, 5).map(batch => (
            <div key={batch.id} className="flex items-center justify-between bg-zinc-950 p-3 rounded-lg border border-zinc-800">
              <div>
                <p className="text-white text-sm">{batch.filename || 'Import CSV'}</p>
                <p className="text-zinc-500 text-xs">
                  {batch.created} cree(s), {batch.updated} mis a jour — {new Date(batch.created_at).toLocaleDateString('fr-FR')}
                </p>
              </div>
              <Button size="sm" variant="outline" className="border-red-500/50 text-red-400 hover:bg-red-500/10 text-xs"
                onClick={() => handleUndo('batch', batch.id)} disabled={undoing === batch.id}
                data-testid={`undo-batch-${batch.id}`}>
                {undoing === batch.id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <AlertTriangle className="w-3 h-3 mr-1" />}
                Annuler
              </Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

const LibraryCompletionTool = () => {
  const [libFile, setLibFile] = useState(null);
  const [libLoading, setLibLoading] = useState(false);
  const [libResult, setLibResult] = useState(null);
  const [libMode, setLibMode] = useState('stock_only');
  const libInputRef = useRef(null);

  const handleLibUpload = async () => {
    if (!libFile) return;
    setLibLoading(true);
    setLibResult(null);
    try {
      const formData = new FormData();
      formData.append('file', libFile);

      const token = localStorage.getItem('pyrostock_token');
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API_URL}/api/exports/complete-library?mode=${libMode}`, {
        method: 'POST',
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: formData,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: 'Erreur serveur' }));
        throw new Error(err.detail || 'Erreur');
      }

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);

      // Also try to peek at the summary by reading the content-disposition
      setLibResult({ url, filename: `final3d_references_manquantes.csv` });
      toast.success('Analyse terminee - fichier pret');
    } catch (err) {
      toast.error(err.message || 'Erreur lors de l\'analyse');
    }
    setLibLoading(false);
  };

  return (
    <Card className="bg-zinc-900/50 border-zinc-800" data-testid="library-completion-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg text-white flex items-center gap-2">
          <RefreshCw className="w-5 h-5 text-amber-400" />
          Completer la bibliotheque Final 3D
        </CardTitle>
        <p className="text-zinc-500 text-xs mt-1">
          Importez l'export Excel de votre bibliotheque Final 3D.
          PyroStock comparera les references avec votre stock et generera un fichier
          au format Final 3D avec les references manquantes, pre-remplies avec les informations
          disponibles (reference, designation, calibre, NEQ, prix, dispositif, couleur, etc.).
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="border-2 border-dashed border-zinc-700 rounded-lg p-6 text-center hover:border-amber-500/50 transition-colors cursor-pointer"
          onClick={() => libInputRef.current?.click()}>
          <input ref={libInputRef} type="file" accept=".xlsx,.xls" className="hidden"
            onChange={(e) => setLibFile(e.target.files?.[0])} />
          {libFile ? (
            <div className="flex items-center justify-center gap-3">
              <FileSpreadsheet className="w-8 h-8 text-green-400" />
              <div className="text-left">
                <p className="text-white font-medium">{libFile.name}</p>
                <p className="text-zinc-500 text-xs">{(libFile.size / 1024).toFixed(0)} Ko</p>
              </div>
            </div>
          ) : (
            <>
              <Upload className="w-8 h-8 mx-auto mb-2 text-zinc-500" />
              <p className="text-zinc-400 text-sm">Export Excel de Final 3D</p>
              <p className="text-zinc-600 text-xs mt-1">.xlsx uniquement</p>
            </>
          )}
        </div>

        {libFile && (
          <>
            <div className="space-y-2">
              <p className="text-zinc-400 text-xs font-medium">Produits a inclure :</p>
              <div className="grid grid-cols-2 gap-2">
                <Button variant={libMode === 'stock_only' ? 'default' : 'outline'} size="sm"
                  className={libMode === 'stock_only' ? 'bg-amber-500 text-black hover:bg-amber-400' : 'border-zinc-700 text-zinc-400'}
                  onClick={() => setLibMode('stock_only')} data-testid="lib-mode-stock">
                  <Package className="w-4 h-4 mr-2" /> En stock uniquement
                </Button>
                <Button variant={libMode === 'all' ? 'default' : 'outline'} size="sm"
                  className={libMode === 'all' ? 'bg-amber-500 text-black hover:bg-amber-400' : 'border-zinc-700 text-zinc-400'}
                  onClick={() => setLibMode('all')} data-testid="lib-mode-all">
                  <FileSpreadsheet className="w-4 h-4 mr-2" /> Tous les produits
                </Button>
              </div>
              <p className="text-zinc-600 text-[10px]">
                {libMode === 'stock_only'
                  ? 'Seuls les produits avec du stock disponible seront exportes vers Final 3D.'
                  : 'Tous les produits actifs de la base PyroStock seront exportes, meme sans stock.'}
              </p>
            </div>
            <Button className="w-full bg-amber-500 hover:bg-amber-400 text-black font-semibold"
              onClick={handleLibUpload} disabled={libLoading} data-testid="analyze-library-btn">
              {libLoading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyse en cours...</>
              ) : (
                <><RefreshCw className="w-4 h-4 mr-2" /> Analyser et completer</>
              )}
            </Button>
          </>
        )}

        {libResult && (
          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 text-center space-y-3">
            <Check className="w-8 h-8 mx-auto text-green-400" />
            <p className="text-green-400 font-medium">Fichier genere avec succes</p>
            <p className="text-zinc-500 text-xs">
              Le fichier contient les references de votre stock absentes de Final 3D,
              formatees pour etre reimportees directement dans la bibliotheque Final 3D.
            </p>
            <a href={libResult.url} download={libResult.filename}>
              <Button className="bg-green-600 hover:bg-green-500 text-white" data-testid="download-library-btn">
                <Download className="w-4 h-4 mr-2" /> Telecharger le fichier complete
              </Button>
            </a>
            <div className="pt-2">
              <Button variant="ghost" size="sm" className="text-zinc-500 text-xs"
                onClick={() => { setLibResult(null); setLibFile(null); }}>
                Nouvelle analyse
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const AccessImportTool = () => {
  const [mdbFile, setMdbFile] = useState(null);
  const [mdbPreview, setMdbPreview] = useState(null);
  const [mdbLoading, setMdbLoading] = useState(false);
  const [mdbImporting, setMdbImporting] = useState(false);
  const [mdbResult, setMdbResult] = useState(null);
  const mdbInputRef = useRef(null);

  const handlePreview = async () => {
    if (!mdbFile) return;
    setMdbLoading(true);
    setMdbPreview(null);
    try {
      const formData = new FormData();
      formData.append('file', mdbFile);
      const token = localStorage.getItem('pyrostock_token');
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API_URL}/api/import/mdb/preview`, {
        method: 'POST',
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: 'Erreur' }));
        throw new Error(err.detail || 'Erreur');
      }
      const data = await res.json();
      setMdbPreview(data);
      toast.success(`${data.table_count} tables detectees dans ${data.filename}`);
    } catch (err) {
      toast.error(err.message || 'Erreur lors de la lecture du fichier Access');
    }
    setMdbLoading(false);
  };

  const handleImport = async () => {
    if (!mdbFile) return;
    setMdbImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', mdbFile);
      const token = localStorage.getItem('pyrostock_token');
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API_URL}/api/import/mdb/execute`, {
        method: 'POST',
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: 'Erreur' }));
        throw new Error(err.detail || 'Erreur');
      }
      const data = await res.json();
      setMdbResult(data);
      toast.success('Import Access termine !');
    } catch (err) {
      toast.error(err.message || 'Erreur lors de l\'import');
    }
    setMdbImporting(false);
  };

  const reset = () => { setMdbFile(null); setMdbPreview(null); setMdbResult(null); };

  return (
    <Card className="bg-zinc-900/50 border-zinc-800" data-testid="access-import-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg text-white flex items-center gap-2">
          <Database className="w-5 h-5 text-cyan-400" />
          Importer une base Access (.mdb)
        </CardTitle>
        <p className="text-zinc-500 text-xs mt-1">
          Importez votre base SuiteArtifeu ou toute autre base Access.
          Les articles, depots, clients, stocks et informations societe seront importes automatiquement.
          Les doublons (meme reference) sont ignores.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {!mdbResult ? (
          <>
            <div className="border-2 border-dashed border-zinc-700 rounded-lg p-6 text-center hover:border-cyan-500/50 transition-colors cursor-pointer"
              onClick={() => mdbInputRef.current?.click()}>
              <input ref={mdbInputRef} type="file" accept=".mdb,.accdb" className="hidden"
                onChange={(e) => { setMdbFile(e.target.files?.[0]); setMdbPreview(null); setMdbResult(null); }} />
              {mdbFile ? (
                <div className="flex items-center justify-center gap-3">
                  <Database className="w-8 h-8 text-cyan-400" />
                  <div className="text-left">
                    <p className="text-white font-medium">{mdbFile.name}</p>
                    <p className="text-zinc-500 text-xs">{(mdbFile.size / 1024 / 1024).toFixed(1)} Mo</p>
                  </div>
                </div>
              ) : (
                <>
                  <Database className="w-8 h-8 mx-auto mb-2 text-zinc-500" />
                  <p className="text-zinc-400 text-sm">Selectionnez votre fichier Access</p>
                  <p className="text-zinc-600 text-xs mt-1">.mdb ou .accdb</p>
                </>
              )}
            </div>

            {mdbFile && !mdbPreview && (
              <Button className="w-full bg-cyan-600 hover:bg-cyan-500 text-white font-semibold"
                onClick={handlePreview} disabled={mdbLoading} data-testid="preview-mdb-btn">
                {mdbLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyse...</> : <><Database className="w-4 h-4 mr-2" /> Analyser le fichier</>}
              </Button>
            )}

            {mdbPreview && (
              <>
                <div className="space-y-2">
                  <p className="text-zinc-300 text-sm font-medium">Tables detectees :</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {mdbPreview.tables.map(t => (
                      <div key={t.name} className="bg-zinc-950 rounded-lg p-3 border border-zinc-800">
                        <p className="text-cyan-400 font-mono text-sm">{t.name}</p>
                        <p className="text-zinc-500 text-xs">{t.row_count} lignes</p>
                      </div>
                    ))}
                  </div>
                </div>
                <Button className="w-full bg-amber-500 hover:bg-amber-400 text-black font-semibold"
                  onClick={handleImport} disabled={mdbImporting} data-testid="execute-mdb-import-btn">
                  {mdbImporting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Import en cours...</> : <><Upload className="w-4 h-4 mr-2" /> Lancer l'import</>}
                </Button>
              </>
            )}
          </>
        ) : (
          <div className="space-y-4">
            <div className="text-center py-4">
              <Check className="w-12 h-12 text-green-400 mx-auto mb-3" />
              <h3 className="text-lg text-white font-heading">Import Access termine</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: 'Articles', count: mdbResult.products, color: 'amber' },
                { label: 'Depots', count: mdbResult.depots, color: 'cyan' },
                { label: 'Clients', count: mdbResult.clients, color: 'green' },
                { label: 'Stock', count: mdbResult.stock, color: 'fuchsia' },
              ].map(s => (
                <div key={s.label} className={`bg-${s.color}-500/10 border border-${s.color}-500/30 rounded-lg p-3 text-center`}>
                  <p className={`text-2xl font-bold text-${s.color}-400`}>{s.count}</p>
                  <p className="text-zinc-400 text-xs">{s.label}</p>
                </div>
              ))}
            </div>
            {mdbResult.errors?.length > 0 && (
              <div className="space-y-1">
                <p className="text-red-400 text-sm font-semibold flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> Erreurs :</p>
                {mdbResult.errors.map((e, i) => <p key={i} className="text-zinc-400 text-xs font-mono">{e}</p>)}
              </div>
            )}
            <Button onClick={reset} variant="outline" className="border-zinc-700 text-zinc-300 w-full">Nouvel import</Button>
            <Button variant="outline" className="border-red-500/50 text-red-400 hover:bg-red-500/10 w-full"
              onClick={async () => {
                try {
                  const token = localStorage.getItem('pyrostock_token');
                  const API_URL = process.env.REACT_APP_BACKEND_URL;
                  const res = await fetch(`${API_URL}/api/import/mdb/undo`, {
                    method: 'POST', credentials: 'include',
                    headers: token ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' },
                  });
                  if (!res.ok) throw new Error((await res.json()).detail || 'Erreur');
                  const data = await res.json();
                  const total = Object.values(data.deleted).reduce((a, b) => a + b, 0);
                  toast.success(`Import Access annule : ${total} element(s) supprime(s)`);
                  reset();
                } catch (err) { toast.error(err.message || 'Erreur annulation'); }
              }}
              data-testid="undo-mdb-import-btn">
              <AlertTriangle className="w-4 h-4 mr-2" /> Annuler l'import Access
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ImportPage;
